import * as React from "react";
import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Button,
  Field,
  Label,
  Select,
  Switch,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import {
  EnrichmentField,
  EnrichmentConfig,
  EnrichmentResponse,
  FIELDS_BY_ROLE,
  AppRole,
  LookupType,
} from "../../../../api/types";
import FieldSelector from "./FieldSelector";
import ThumbnailSettings from "./ThumbnailSettings";
import ProgressIndicator, { EnrichmentStep } from "./ProgressIndicator";
import ResultsPanel from "./ResultsPanel";
import ErrorBanner from "../../shared/ErrorBanner";
import Section from "../../shared/Section";
import ColumnInput from "../../shared/ColumnInput";
import RowInput from "../../shared/RowInput";
import { loadEnrichmentSettings, saveEnrichmentSettings } from "./useEnrichmentSettings";

export interface EnrichmentFormProps {
  role: AppRole;
  onEnrich: (
    config: EnrichmentConfig,
    signal: AbortSignal,
    reportStep: (step: EnrichmentStep) => void
  ) => Promise<EnrichmentResponse>;
}

const useStyles = makeStyles({
  container: {
    padding: "16px"
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  rowGroup: {
    display: "flex",
    gap: "12px",
  },
  rowField: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    paddingTop: "8px",
  },
  accordionHeaderButton: {
    padding: 0
  },
  sectionLabel: {
    display: "block",
    fontWeight: tokens.fontWeightSemibold,
    marginBottom: "4px",
  },
});

/** Returns the next column letter(s) after the given one (A→B, Z→AA). */
function nextColumn(col: string): string {
  const chars = col.toUpperCase().split("");
  let carry = true;
  for (let i = chars.length - 1; i >= 0 && carry; i--) {
    const code = chars[i].charCodeAt(0) + 1;
    if (code > 90) {
      // 'Z'
      chars[i] = "A";
    } else {
      chars[i] = String.fromCharCode(code);
      carry = false;
    }
  }
  if (carry) {
    chars.unshift("A");
  }
  return chars.join("");
}

import { isValidColumn } from "../../../../utils/columnUtils";

/** Builds a default column mapping starting from the column after articleCodeColumn. */
function buildDefaultMapping(
  fields: EnrichmentField[],
  articleCodeColumn: string
): Record<EnrichmentField, string> {
  const mapping: Partial<Record<EnrichmentField, string>> = {};
  let col = nextColumn(articleCodeColumn);
  for (const field of fields) {
    mapping[field] = col;
    col = nextColumn(col);
  }
  return mapping as Record<EnrichmentField, string>;
}

const EnrichmentForm: React.FC<EnrichmentFormProps> = ({ role, onEnrich }) => {
  const styles = useStyles();

  // Form state — restore from saved settings if available
  const availableFields = FIELDS_BY_ROLE[role];
  const [saved] = React.useState(() => loadEnrichmentSettings(role));
  const [startRow, setStartRow] = React.useState(saved?.startRow ?? 2);
  const [endRow, setEndRow] = React.useState(saved?.endRow ?? 100);
  const [articleCodeColumn, setArticleCodeColumn] = React.useState(saved?.articleCodeColumn ?? "A");
  const [lookupType, setLookupType] = React.useState<LookupType>(saved?.lookupType ?? "ArticleCode");
  const [selectedFields, setSelectedFields] = React.useState<EnrichmentField[]>(
    saved?.selectedFields ?? [...availableFields]
  );
  const [fieldColumnMapping, setFieldColumnMapping] = React.useState<Record<EnrichmentField, string>>(
    () => saved?.fieldColumnMapping ?? buildDefaultMapping(availableFields, "A")
  );
  const [thumbnailSize, setThumbnailSize] = React.useState(saved?.thumbnailSize ?? 64);
  const [fitToCell, setFitToCell] = React.useState(saved?.fitToCell ?? true);
  const [noChangeIfNoResults, setNoChangeIfNoResults] = React.useState(saved?.noChangeIfNoResults ?? true);

  // Persist settings on every change
  React.useEffect(() => {
    saveEnrichmentSettings(role, {
      startRow,
      endRow,
      articleCodeColumn,
      lookupType,
      selectedFields,
      fieldColumnMapping,
      thumbnailSize,
      fitToCell,
      noChangeIfNoResults,
    });
  }, [role, startRow, endRow, articleCodeColumn, lookupType, selectedFields, fieldColumnMapping, thumbnailSize, fitToCell, noChangeIfNoResults]);

  // Workflow state
  const [isRunning, setIsRunning] = React.useState(false);
  const [step, setStep] = React.useState<EnrichmentStep | null>(null);
  const [result, setResult] = React.useState<EnrichmentResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // Recalculate default column mapping when article code column changes
  const handleArticleCodeColumnChange = (value: string) => {
    setArticleCodeColumn(value);
    if (isValidColumn(value)) {
      setFieldColumnMapping(buildDefaultMapping(availableFields, value));
    }
  };

  // Update a single field's column mapping
  const handleColumnMappingChange = (field: EnrichmentField, value: string) => {
    setFieldColumnMapping((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // All active columns for row highlight feedback
  const activeColumns = React.useMemo(() => {
    const cols = [articleCodeColumn];
    for (const field of selectedFields) {
      const col = fieldColumnMapping[field];
      if (col) cols.push(col);
    }
    return cols;
  }, [articleCodeColumn, selectedFields, fieldColumnMapping]);

  // Check if form is valid
  const isFormValid =
    startRow >= 1 &&
    endRow >= startRow &&
    isValidColumn(articleCodeColumn) &&
    selectedFields.length > 0 &&
    selectedFields.every((f) => isValidColumn(fieldColumnMapping[f] || ""));

  // Run the enrichment
  const handleEnrich = async () => {
    setError(null);
    setResult(null);
    setIsRunning(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const config: EnrichmentConfig = {
      startRow,
      endRow,
      articleCodeColumn,
      lookupType,
      selectedFields,
      fieldColumnMapping,
      thumbnailSize,
      fitToCell,
      noChangeIfNoResults,
    };

    try {
      // The parent's onEnrich handles orchestration and calls setStep via callback
      const response = await onEnrich(config, controller.signal, setStep);
      setResult(response);
      setStep("done");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setError("Enrichment was cancelled.");
      } else {
        setError(err instanceof Error ? err.message : "An unknown error occurred.");
      }
    } finally {
      setIsRunning(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancel = () => {
    abortControllerRef.current?.abort();
    setStep(null);
  };

  const handleDismissResults = () => {
    setResult(null);
    setStep(null);
    setError(null);
  };

  // If we have results, show the results panel
  if (result) {
    return (
      <div className={styles.container}>
        <ResultsPanel response={result} onDismiss={handleDismissResults} />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {error &&
        <Section>
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
        </Section>
      }

      <Section>
        <Label className={styles.sectionLabel}>Range</Label>
        <div className={styles.form}>
          {/* Row range */}
          <div className={styles.rowGroup}>
            <div className={styles.rowField}>
              <Field label="Start row">
                <RowInput
                  value={startRow}
                  onChange={setStartRow}
                  min={1}
                  columns={activeColumns}
                  disabled={isRunning}
                />
              </Field>
            </div>
          </div>

          <div className={styles.rowGroup}>
            <div className={styles.rowField}>
              <Field label="End row">
                <RowInput
                  value={endRow}
                  onChange={setEndRow}
                  min={startRow}
                  columns={activeColumns}
                  disabled={isRunning}
                />
              </Field>
            </div>
          </div>

          {/* Lookup type + input column */}
          <div className={styles.rowGroup}>
            <div className={styles.rowField}>
              <Field label="Lookup type">
                <Select
                  value={lookupType}
                  onChange={(_e, data) => setLookupType(data.value as LookupType)}
                  disabled={isRunning}
                >
                  <option value="ArticleCode">Article Code</option>
                  <option value="Barcode">Barcode</option>
                </Select>
              </Field>
            </div>
            <div className={styles.rowField}>
              <Field label={lookupType === "Barcode" ? "Barcode column" : "Article code column"}>
                <ColumnInput
                  value={articleCodeColumn}
                  onChange={handleArticleCodeColumnChange}
                  startRow={startRow}
                  endRow={endRow}
                  disabled={isRunning}
                />
              </Field>
            </div>
          </div>
        </div>
      </Section>

      <Section>
        <Label className={styles.sectionLabel}>Settings</Label>
        <Accordion defaultOpenItems={[]} collapsible>
          <AccordionItem value="fields">
            <AccordionHeader button={{ className: styles.accordionHeaderButton }}>Fields & Columns</AccordionHeader>
            <AccordionPanel>
              <FieldSelector
                role={role}
                selectedFields={selectedFields}
                onChange={setSelectedFields}
                fieldColumnMapping={fieldColumnMapping}
                onColumnMappingChange={handleColumnMappingChange}
                startRow={startRow}
                endRow={endRow}
                disabled={isRunning}
              />
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem value="thumbnail">
            <AccordionHeader button={{ className: styles.accordionHeaderButton }}>Thumbnails</AccordionHeader>
            <AccordionPanel>
              <ThumbnailSettings
                thumbnailSize={thumbnailSize}
                fitToCell={fitToCell}
                onSizeChange={setThumbnailSize}
                onFitToCellChange={setFitToCell}
                disabled={isRunning}
              />
            </AccordionPanel>
          </AccordionItem>

          <AccordionItem value="advanced">
            <AccordionHeader button={{ className: styles.accordionHeaderButton }}>Advanced</AccordionHeader>
            <AccordionPanel>
              <Switch
                label="No changes if no results"
                checked={noChangeIfNoResults}
                onChange={(_e, data) => setNoChangeIfNoResults(data.checked)}
                disabled={isRunning}
              />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Section>


      <Section>
        {/* Progress */}
        {isRunning && step && <ProgressIndicator step={step} />}

        {/* Actions */}
        <div className={styles.actions}>
          <Button
            appearance="primary"
            onClick={handleEnrich}
            disabled={isRunning || !isFormValid}
          >
            Enrich Articles
          </Button>
          {isRunning && (
            <Button
              appearance="secondary"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          )}
        </div>
      </Section >
    </div>
  );
};

export { EnrichmentStep };
export default EnrichmentForm;
